import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useNavigate } from 'react-router-dom';
import { usePublicAuth } from '../../context/PublicAuthContext';
import { useAlert } from '../../context/AlertContext';
import PublicLayout from '../../layouts/PublicLayout';
import { Camera, Save, X, Trash2, Edit2, Plus } from 'lucide-react';

const ClientProfilePage = () => {
    const { client, clientToken, logoutClient, loginClient } = usePublicAuth();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const [dependents, setDependents] = useState<any[]>([]);

    // Form Data
    const [name, setName] = useState(client?.name || '');
    const [photo, setPhoto] = useState<File | null>(null);

    const getInitialPhoto = () => {
        if (!client?.photo) return null;
        if (client.photo.startsWith('http')) return client.photo;
        return `${API_BASE_URL}${client.photo}`;
    };

    const [preview, setPreview] = useState(getInitialPhoto());

    // Dependent Form
    const [editingDeptId, setEditingDeptId] = useState<number | null>(null);
    const [newDeptName, setNewDeptName] = useState('');
    const [editingDeptName, setEditingDeptName] = useState('');

    useEffect(() => {
        if (!clientToken) {
            navigate('/public/menu'); // Should handle redirection to login request if not auth
            return;
        }
        fetchDependents();
    }, [clientToken, navigate]);

    // Update preview when client data loads if it wasn't available initially
    useEffect(() => {
        if (client?.photo && !preview && !photo) {
            setPreview(getInitialPhoto());
        }
        if (client?.name && !name) {
            setName(client.name);
        }
    }, [client, photo]);

    const handleApiError = (err: any, defaultMsg?: string) => {
        console.error(err);
        if (axios.isAxiosError(err) && err.response?.status === 401) {
            logoutClient();
            showAlert('Sessão expirada. Por favor, solicite um novo link de acesso.', { type: 'warning' });
            navigate('/public/menu');
        } else if (defaultMsg) {
            showAlert(defaultMsg, { type: 'error' });
        }
    };

    const fetchDependents = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/public/client/dependents/`, {
                headers: { Authorization: `Bearer ${clientToken}` }
            });
            setDependents(res.data);
        } catch (err) {
            handleApiError(err);
        }
    };

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const formData = new FormData();
        formData.append('name', name);
        if (photo) {
            formData.append('photo', photo);
        }

        try {
            const res = await axios.put(`${API_BASE_URL}/api/public/client/profile/`, formData, {
                headers: {
                    Authorization: `Bearer ${clientToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            // Update context
            loginClient(clientToken!, res.data); // Keep token, update data
            showAlert('Perfil atualizado com sucesso!', { type: 'success' });
        } catch (err) {
            handleApiError(err, 'Erro ao atualizar perfil.');
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setPhoto(e.target.files[0]);
            setPreview(URL.createObjectURL(e.target.files[0]));
        }
    };

    const handleAddDependent = async () => {
        if (!newDeptName.trim()) return;
        try {
            await axios.post(`${API_BASE_URL}/api/public/client/dependents/`, { name: newDeptName.toUpperCase() }, {
                headers: { Authorization: `Bearer ${clientToken}` }
            });
            fetchDependents();
            setNewDeptName('');
            showAlert('Dependente adicionado com sucesso!', { type: 'success' });
        } catch (err) {
            handleApiError(err, 'Erro ao adicionar dependente');
        }
    };

    const handleSaveEdit = async (id: number) => {
        if (!editingDeptName.trim()) return;
        try {
            await axios.put(`${API_BASE_URL}/api/public/client/dependents/${id}/`, { name: editingDeptName.toUpperCase() }, {
                headers: { Authorization: `Bearer ${clientToken}` }
            });
            fetchDependents();
            setEditingDeptId(null);
            setEditingDeptName('');
            showAlert('Dependente atualizado com sucesso!', { type: 'success' });
        } catch (err) {
            handleApiError(err, 'Erro ao atualizar dependente');
        }
    };

    const startEditing = (dept: any) => {
        setEditingDeptId(dept.id);
        setEditingDeptName(dept.name);
    };

    const cancelEditing = () => {
        setEditingDeptId(null);
        setEditingDeptName('');
    };

    const handleDeleteDependent = (id: number) => {
        showAlert('Tem certeza que deseja remover este dependente?', {
            title: 'Confirmação',
            confirmable: true,
            confirmText: 'Remover',
            confirmVariant: 'btn-primary',
            onConfirm: async () => {
                try {
                    await axios.delete(`${API_BASE_URL}/api/public/client/dependents/${id}/`, {
                        headers: { Authorization: `Bearer ${clientToken}` }
                    });
                    fetchDependents();
                    showAlert('Dependente removido com sucesso!', { type: 'success' });
                } catch (err) {
                    handleApiError(err, 'Erro ao remover dependente');
                }
            }
        });
    };

    return (
        <PublicLayout>
            <div className="container py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>Meu Perfil</h2>
                    <button className="btn btn-outline-secondary" onClick={() => navigate('/public/menu?phone=' + client?.phone)}>
                        Voltar
                    </button>
                </div>

                <div className="row">
                    {/* Profile Section */}
                    <div className="col-md-6 mb-4">
                        <div className="card shadow-sm">
                            <div className="card-header bg-white">
                                <h4 className="mb-0">Dados Pessoais</h4>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleUpdateProfile}>
                                    <div className="mb-3 text-center">
                                        <div style={{ width: '100px', height: '100px', margin: '0 auto', marginBottom: '1rem', position: 'relative' }}>
                                            {preview ? (
                                                <img src={preview} alt="Profile" className="rounded-circle w-100 h-100 object-fit-cover" />
                                            ) : (
                                                <div className="rounded-circle bg-light w-100 h-100 d-flex align-items-center justify-content-center border">
                                                    <span className="fs-1 text-muted">{name.charAt(0)}</span>
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                id="photo-upload"
                                                className="d-none"
                                                onChange={handlePhotoChange}
                                            />
                                            <label
                                                htmlFor="photo-upload"
                                                className="btn btn-sm btn-light border position-absolute bottom-0 end-0 rounded-circle p-2"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Camera size={20} />
                                            </label>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Nome Completo</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={name}
                                            onChange={(e) => setName(e.target.value.toUpperCase())}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Telefone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={client?.phone}
                                            disabled
                                        />
                                        <div className="form-text">Para alterar o telefone, entre em contato com o suporte.</div>
                                    </div>

                                    <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                                        {loading ? 'Salvando...' : 'Salvar Alterações'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Dependents Section */}
                    <div className="col-md-6 mb-4">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-white">
                                <h4 className="mb-0">Dependentes</h4>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">Adicionar Dependente</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newDeptName}
                                            onChange={(e) => setNewDeptName(e.target.value.toUpperCase())}
                                            placeholder="Nome do dependente"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') handleAddDependent();
                                            }}
                                        />
                                        <button className="btn btn-success" onClick={handleAddDependent}>
                                            <Plus size={18} /> Adicionar
                                        </button>
                                    </div>
                                </div>

                                {dependents.length === 0 && (
                                    <p className="text-muted text-center py-4">Nenhum dependente cadastrado.</p>
                                )}

                                {dependents.length > 0 && (
                                    <div className="table-responsive custom-table-container">
                                        <table className="table hover custom-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Nome</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dependents.map(dept => (
                                                    <tr key={dept.id}>
                                                        <td className="align-middle">
                                                            <div className="row mx-0">
                                                                {editingDeptId === dept.id ? (
                                                                    <>
                                                                        <div className="row p-0">
                                                                            <div className="col">
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-control form-control-sm flex-grow-1"
                                                                                    value={editingDeptName}
                                                                                    onChange={(e) => setEditingDeptName(e.target.value.toUpperCase())}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="row p-0 my-3">
                                                                            <div className="col-auto">
                                                                                <button className="btn form-control btn-primary" onClick={() => handleSaveEdit(dept.id)}>
                                                                                    <Save size={16} /> Salvar
                                                                                </button>
                                                                            </div>
                                                                            <div className="col-auto">
                                                                                <button className="btn form-control btn-secondary" onClick={cancelEditing}>
                                                                                    <X size={16} /> Cancelar
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div className="col" onClick={() => startEditing(dept)}>
                                                                        <span
                                                                            style={{ cursor: 'pointer', width: '100%' }}
                                                                        >
                                                                            {dept.name}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {!editingDeptId && (
                                                                    <div className="col-1 text-end align-middle">
                                                                        <button
                                                                            className="btn btn-sm btn-link text-secondary p-0"
                                                                            onClick={() => handleDeleteDependent(dept.id)}
                                                                            title="Excluir"
                                                                        >
                                                                            <Trash2 size={18} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>

                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="text-center mt-4">
                    <button className="btn btn-link text-danger" onClick={() => {
                        logoutClient();
                        navigate('/public/menu');
                    }}>
                        Sair do meu perfil
                    </button>
                </div>
            </div>
        </PublicLayout>
    );
};

export default ClientProfilePage;
